import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { UserSquare2, Loader2, Plus, X, Film } from "lucide-react";
import { toast } from "sonner";
import AutocompleteInput from "@/components/AutocompleteInput";
import DeleteButton from "@/components/DeleteButton";

/**
 * `/owners` — top-level owners index. Similar shape to associations —
 * union of the explicit `owners` collection and every distinct owner_name
 * across clips. Horses roll up under owners.
 */
export default function OwnersIndex() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [horses, setHorses] = useState([]);
  const [horsesLoading, setHorsesLoading] = useState(true);
  const [horseQ, setHorseQ] = useState("");

  const load = () => {
    setLoading(true);
    return api.get("/owners")
      .then((r) => setRows(r.data.owners || []))
      .finally(() => setLoading(false));
  };

  const loadHorses = () => {
    setHorsesLoading(true);
    return api.get("/horses")
      .then((r) => setHorses(r.data.horses || []))
      .finally(() => setHorsesLoading(false));
  };

  useEffect(() => { load(); loadHorses(); }, []);

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-tiny mb-2">Owners</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95] mb-1">Owners</h1>
          <p className="text-stone-400 text-sm mt-3 max-w-2xl">
            People and outfits who own horses. Every horse rolls up under one owner —
            one owner can carry many horses across many events.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary inline-flex items-center gap-1.5 whitespace-nowrap"
          data-testid="new-owner-btn"
        >
          <Plus size={14} /> New Owner
        </button>
      </div>

      {loading ? (
        <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-stone-700 p-8 text-center text-stone-500" data-testid="owners-empty">
          No owners yet. Click <strong className="text-orange-400">New Owner</strong> to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="owners-grid">
          {rows.map((o) => (
            <Link
              key={o.name}
              to={`/owners/${encodeURIComponent(o.name)}`}
              className="border border-stone-800 hover:border-orange-700 p-5 transition-colors group"
              data-testid={`owner-card-${o.name.replace(/\s+/g, "_")}`}
            >
              <div className="flex items-start justify-between mb-3">
                <UserSquare2 size={22} className="text-orange-500 shrink-0" />
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500 text-right leading-tight">
                    {o.horse_count} horse{o.horse_count === 1 ? "" : "s"}
                  </span>
                  <DeleteButton
                    testId={`delete-owner-${o.name.replace(/\s+/g, "_")}`}
                    label={o.name}
                    onDelete={async () => {
                      const r = await api.delete(`/owners/${encodeURIComponent(o.name)}`);
                      load();
                      return r;
                    }}
                    successMessage={`"${o.name}" removed`}
                  />
                </div>
              </div>
              <div className="font-display uppercase text-xl font-semibold leading-tight group-hover:text-orange-400 truncate">
                {o.name}
              </div>
              {o.description && (
                <div className="text-xs text-stone-500 mt-1 line-clamp-2">{o.description}</div>
              )}
              <div className="text-xs text-stone-400 mt-3 inline-flex items-center gap-1 font-mono-tc">
                <Film size={11} /> {o.clip_count} clips
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateOwnerModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}

      {/* ─────── All horses (flat view across every owner) ─────── */}
      <div className="mt-14 pt-8 border-t border-stone-800">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="label-tiny mb-2">All horses</div>
            <h2 className="font-display uppercase text-3xl font-bold leading-[0.95]">Horses</h2>
            <p className="text-stone-500 text-xs mt-2 max-w-xl">
              Every horse across every owner — breed, current owner, and clip count in one view.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={horseQ}
              onChange={(e) => setHorseQ(e.target.value)}
              placeholder="Search horses…"
              className="field-input text-xs w-56"
              data-testid="horses-search"
            />
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-mono-tc">
              {horses.length} total
            </span>
          </div>
        </div>

        {horsesLoading ? (
          <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
            <Loader2 size={13} className="animate-spin" /> Loading horses…
          </div>
        ) : horses.length === 0 ? (
          <div className="border border-dashed border-stone-700 p-6 text-center text-stone-500 text-sm" data-testid="horses-empty">
            No horses yet. Open an owner and click <strong className="text-orange-400">+ Add horse</strong>.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="horses-grid">
            {horses
              .filter((h) => {
                const q = horseQ.trim().toLowerCase();
                if (!q) return true;
                return (
                  (h.name || "").toLowerCase().includes(q) ||
                  (h.owner_name || "").toLowerCase().includes(q) ||
                  (h.breed || "").toLowerCase().includes(q)
                );
              })
              .map((h) => (
                <Link
                  key={h.name}
                  to={`/horses/${encodeURIComponent(h.name)}`}
                  className="border border-stone-800 hover:border-orange-700 p-3 transition-colors group"
                  data-testid={`horse-card-${h.name.replace(/\s+/g, "_")}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-lg leading-none">🐎</span>
                    <span className="text-[10px] uppercase tracking-widest text-stone-500 font-mono-tc">
                      {h.clip_count} clip{h.clip_count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="font-display uppercase text-sm font-semibold leading-tight group-hover:text-orange-400 truncate">
                    {h.name}
                  </div>
                  {h.breed && (
                    <div className="text-[11px] text-stone-500 mt-1 truncate">
                      {h.breed}
                    </div>
                  )}
                  <div className="text-[11px] text-stone-400 mt-1 truncate">
                    {h.owner_name ? (
                      <span className="text-stone-400">
                        owner: <span className="text-orange-400/80">{h.owner_name}</span>
                      </span>
                    ) : (
                      <span className="text-stone-600 italic">unassigned</span>
                    )}
                  </div>
                </Link>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateOwnerModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/owners", { name: name.trim(), description: description.trim() });
      toast.success(`Created "${name.trim()}"`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-owner-modal">
      <div className="bg-stone-950 border border-orange-700 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display uppercase text-2xl font-bold">New Owner</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-tiny block mb-1">Name</label>
            <AutocompleteInput
              value={name}
              onChange={setName}
              field="owner_name"
              placeholder="e.g. Smith Ranch, Rocking Horse Stables"
              className="field-input w-full"
              data-testid="new-owner-name"
              autoFocus
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">Description (optional)</label>
            <AutocompleteInput
              as="textarea"
              field="description"
              value={description}
              onChange={setDescription}
              rows={3}
              className="field-input w-full resize-none"
              placeholder="Contact info, brand notes, anything you'd like admins to see."
              data-testid="new-owner-description"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
            data-testid="new-owner-submit"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            {busy ? "Creating…" : "Create Owner"}
          </button>
        </form>
      </div>
    </div>
  );
}
